import type { WeaponId } from "./schemas";
import { WEAPONS } from "./weapons";

export type RecipeKind = "frame" | "weapon" | "cosmetic" | "blueprint";

export type CraftRecipe = {
  id: string;
  name: string;
  kind: RecipeKind;
  description: string;
  /** Banked salvage cost. */
  salvageCost: number;
  /** Pilot rank required before this design can be fabricated. */
  requiresRank?: number;
  /** Optional elite-core blueprint requirement (banked blueprints array). */
  requiresBlueprint?: string;
  unlocksFrame?: string;
  unlocksWeapon?: WeaponId;
  unlocksCosmetic?: string;
  grantsBlueprint?: string;
};

function weaponRecipe(
  id: string,
  weaponId: WeaponId,
  salvageCost: number,
  requiresRank?: number,
  requiresBlueprint?: string,
): CraftRecipe {
  const w = WEAPONS[weaponId];
  return {
    id,
    name: w.name,
    kind: "weapon",
    description: `${w.slot} — ${w.tradeoff}. ${w.description}`,
    salvageCost,
    requiresRank,
    requiresBlueprint,
    unlocksWeapon: weaponId,
  };
}

/** All recipes are craftable now — no deferred / coming-soon entries. */
export const RECIPES: CraftRecipe[] = [
  {
    id: "frame_hauler",
    name: "Hauler Frame",
    kind: "frame",
    description: "Unlock Hauler — more cargo, slower cruise.",
    salvageCost: 80,
    requiresRank: 2,
    unlocksFrame: "hauler",
  },
  {
    id: "frame_interceptor",
    name: "Interceptor Frame",
    kind: "frame",
    description: "Unlock Interceptor — extreme speed, thin hull.",
    salvageCost: 100,
    requiresRank: 5,
    unlocksFrame: "interceptor",
  },
  {
    id: "frame_bulwark",
    name: "Bulwark Frame",
    kind: "frame",
    description: "Unlock Bulwark — heavy armor, slow boost regen.",
    salvageCost: 140,
    requiresRank: 8,
    unlocksFrame: "bulwark",
  },
  {
    id: "frame_vanguard",
    name: "Vanguard Frame",
    kind: "frame",
    description: "Unlock Vanguard — heavy gunship damage with slower weapon cycling.",
    salvageCost: 520,
    requiresRank: 16,
    unlocksFrame: "vanguard",
  },
  {
    id: "frame_reclaimer",
    name: "Reclaimer Frame",
    kind: "frame",
    description: "Unlock Reclaimer — triples rejected-part salvage with reduced weapon output.",
    salvageCost: 900,
    requiresRank: 24,
    unlocksFrame: "reclaimer",
  },
  {
    id: "frame_corsair",
    name: "Corsair Frame",
    kind: "frame",
    description: "Unlock Corsair — carried scrap charges its weapons up to a 25% bonus.",
    salvageCost: 1250,
    requiresRank: 30,
    unlocksFrame: "corsair",
  },
  {
    id: "frame_fluxrunner",
    name: "Fluxrunner Frame",
    kind: "frame",
    description:
      "Unlock Fluxrunner — converts boost fuel into shields under fire, but burns fuel faster and reduces weapon output.",
    salvageCost: 2200,
    requiresRank: 40,
    unlocksFrame: "fluxrunner",
  },
  {
    id: "frame_jackal",
    name: "Jackal Frame",
    kind: "frame",
    description:
      "Unlock Jackal — each kill restores 12% boost fuel, with thin shields and reduced cargo.",
    salvageCost: 1650,
    requiresRank: 36,
    unlocksFrame: "jackal",
  },
  {
    id: "frame_slipstream",
    name: "Slipstream Frame",
    kind: "frame",
    description:
      "Unlock Slipstream — boost ignition cuts active weapon cooldowns by 35%, with a light hull and small cargo bay.",
    salvageCost: 5600,
    requiresRank: 56,
    unlocksFrame: "slipstream",
  },
  {
    id: "frame_backlash",
    name: "Backlash Frame",
    kind: "frame",
    description:
      "Unlock Backlash — shield breaks advance every active weapon cycle by 65%, with a thin hull and reduced cargo.",
    salvageCost: 12_600,
    requiresRank: 68,
    unlocksFrame: "backlash",
  },
  {
    id: "frame_magpie",
    name: "Magpie Frame",
    kind: "frame",
    description:
      "Unlock Magpie — each salvage recovery advances the equipped ordnance cycle by 35%, with light plating and reduced weapon output.",
    salvageCost: 24_000,
    requiresRank: 78,
    unlocksFrame: "magpie",
  },
  {
    id: "frame_breachwing",
    name: "Breachwing Frame",
    kind: "frame",
    description:
      "Unlock Breachwing — every confirmed enemy-shield break advances all equipped weapon cycles by 20%, with light shields and reduced cargo.",
    salvageCost: 38_000,
    requiresRank: 86,
    unlocksFrame: "breachwing",
  },
  {
    id: "frame_tetherwing",
    name: "Tetherwing Frame",
    kind: "frame",
    description:
      "Unlock Tetherwing — catching a returning projectile advances all weapon cycles by 30% and restores 12% boost, with light armor and reduced cargo.",
    salvageCost: 75_000,
    requiresRank: 96,
    unlocksFrame: "tetherwing",
  },
  weaponRecipe("weapon_kinetic_slug", "kinetic_slug", 70, 2),
  weaponRecipe("weapon_kinetic_burst", "kinetic_burst", 90, 3),
  weaponRecipe("weapon_needle_array", "needle_array", 380, 14),
  weaponRecipe("weapon_cascade_cannon", "cascade_cannon", 3200, 48),
  weaponRecipe("weapon_aegis_drill", "aegis_drill", 32_000, 84),
  weaponRecipe("weapon_arc_fork", "arc_fork", 110, 4),
  weaponRecipe("weapon_arc_pulse", "arc_pulse", 150, 6),
  weaponRecipe("weapon_phase_rail", "phase_rail", 680, 20),
  weaponRecipe("weapon_vortex_disc", "vortex_disc", 1500, 32),
  weaponRecipe("weapon_arc_web", "arc_web", 4800, 52),
  weaponRecipe("weapon_grav_harpoon", "grav_harpoon", 15_600, 72),
  weaponRecipe("weapon_crown_saw", "crown_saw", 60_000, 94),
  weaponRecipe("weapon_breach", "breach_torpedo", 100, 3),
  weaponRecipe("weapon_swarm", "swarm_microtorps", 180, 9),
  weaponRecipe("weapon_grav", "grav_charge", 260, 12),
  weaponRecipe("weapon_implosion", "implosion_charge", 1850, 38),
  weaponRecipe("weapon_foundry_breaker", "foundry_breaker", 8400, 62),
  {
    id: "trail_ion",
    name: "Ion Trail Kit",
    kind: "cosmetic",
    description: "Cosmetic ion exhaust trail (no combat power).",
    salvageCost: 40,
    requiresRank: 2,
    unlocksCosmetic: "trail_ion",
  },
  {
    id: "trail_rust",
    name: "Rust Trail Kit",
    kind: "cosmetic",
    description: "Cosmetic rust plume trail (no combat power).",
    salvageCost: 40,
    requiresRank: 4,
    unlocksCosmetic: "trail_rust",
  },
  {
    id: "bp_elite_line",
    name: "Elite Core Line",
    kind: "blueprint",
    description: "Bank a blueprint from an elite core (or buy the line).",
    salvageCost: 120,
    requiresRank: 7,
    grantsBlueprint: "elite_core_line",
  },
  {
    id: "bp_blast_jacket",
    name: "Blast Jacket Line",
    kind: "blueprint",
    description:
      "Unlock the Blast Jacket ordnance powerup in level-up offers for every equipped warhead.",
    salvageCost: 2600,
    requiresRank: 44,
    grantsBlueprint: "blast_jacket_line",
  },
  {
    id: "bp_duplex_rack",
    name: "Duplex Rack Line",
    kind: "blueprint",
    description:
      "Unlock the Duplex Rack ordnance powerup: paired warheads with split aim and reduced yield.",
    salvageCost: 4000,
    requiresRank: 50,
    grantsBlueprint: "duplex_rack_line",
  },
  {
    id: "bp_mirror_coupler",
    name: "Mirror Coupler Line",
    kind: "blueprint",
    description:
      "Unlock the Mirror Coupler secondary powerup: one mirrored extra bolt with reduced yield and a slower reset.",
    salvageCost: 6800,
    requiresRank: 58,
    grantsBlueprint: "mirror_coupler_line",
  },
  {
    id: "bp_impact_mill",
    name: "Impact Mill Line",
    kind: "blueprint",
    description:
      "Unlock the Impact Mill primary powerup: every direct impact ejects radial shards at reduced yield.",
    salvageCost: 10400,
    requiresRank: 64,
    grantsBlueprint: "impact_mill_line",
  },
  {
    id: "bp_crossfeed_relay",
    name: "Crossfeed Relay Line",
    kind: "blueprint",
    description:
      "Unlock the Crossfeed Relay powerup: kills with its fitted slot advance every other weapon cycle.",
    salvageCost: 19_500,
    requiresRank: 74,
    grantsBlueprint: "crossfeed_relay_line",
  },
  {
    id: "bp_crown_array",
    name: "Crown Array Line",
    kind: "blueprint",
    description:
      "Unlock the stackable Crown Array powerup for any weapon slot: more diverging shots traded for lower yield and a slower reset.",
    salvageCost: 28_500,
    requiresRank: 82,
    grantsBlueprint: "crown_array_line",
  },
  {
    id: "bp_capacitor_tap",
    name: "Capacitor Tap Line",
    kind: "blueprint",
    description:
      "Unlock the stackable Capacitor Tap powerup for any weapon slot: enemy-shield damage restores your shields at the cost of cycle speed.",
    salvageCost: 48_000,
    requiresRank: 92,
    grantsBlueprint: "capacitor_tap_line",
  },
  {
    id: "bp_recall_spool",
    name: "Recall Spool Line",
    kind: "blueprint",
    description:
      "Unlock the Recall Spool secondary powerup: any secondary projectile gains a damaging return leg at the cost of cycle speed.",
    salvageCost: 95_000,
    requiresRank: 98,
    grantsBlueprint: "recall_spool_line",
  },
  {
    id: "bp_counterpulse_emitter",
    name: "Centurion Counterpulse Line",
    kind: "blueprint",
    description:
      "Unlock the Centurion Counterpulse ordnance powerup: blasts erase hostile rounds within their radius at the cost of yield and cycle speed.",
    salvageCost: 120_000,
    requiresRank: 100,
    grantsBlueprint: "counterpulse_emitter_line",
  },
  {
    id: "frame_bulwark_elite",
    name: "Bulwark Elite Plating",
    kind: "frame",
    description: "Craft Bulwark cheaper with elite_core_line blueprint.",
    salvageCost: 70,
    requiresRank: 8,
    requiresBlueprint: "elite_core_line",
    unlocksFrame: "bulwark",
  },
  weaponRecipe("weapon_grav_elite", "grav_charge", 140, 12, "elite_core_line"),
];

export function recipeById(id: string): CraftRecipe | undefined {
  return RECIPES.find((r) => r.id === id);
}

/** Weapon recipes listed first for Carrier Bay clarity. */
export function recipesByKind(kind: RecipeKind): CraftRecipe[] {
  return RECIPES.filter((r) => r.kind === kind);
}
